import type { EditorState } from "@/domain/vim/model/editor-state";
import { type Keystroke, keystrokeNotation } from "@/domain/vim/model/keystroke";

/**
 * 打鍵の帯（ui-design.md §5.4）の1枠が取りうる状態。
 * `KeyCapState`（既習/保留/未習/修飾）と対応する。
 */
export type StripFrameState = "learned" | "pending" | "unlearned" | "modifier";

export interface StripFrame {
  readonly id: number;
  readonly label: string;
  readonly state: StripFrameState;
}

/** 直近12打のみを帯に流す（ui-design.md §5.4）。 */
export const STRIP_CAPACITY = 12;

export type KeystrokeOutcome =
  | { readonly kind: "unlearned"; readonly label: string }
  | { readonly kind: "pending"; readonly label: string }
  | { readonly kind: "confirmed"; readonly label: string }
  /**
   * Shift/Ctrl/Alt/Meta の単独押下。JIS配列では `$`（Shift+4）・`*`（Shift+:）・`?`（Shift+/）
   * のように**修飾キーを先に押さなければ打てない技**が多く、これを「未習の技」として
   * 咎めるのは誤りである。手数には数えないが、未習の警告も出さない中立の状態として扱う。
   */
  | { readonly kind: "modifier"; readonly label: string };

function keysToLabel(keys: readonly Keystroke[]): string {
  return keys.map(keystrokeNotation).join("");
}

/**
 * 修飾キー単独の押下（vimエンジンには渡らない）を帯に表すためのラベル。
 * 手数には数えないが、未習の破線ではなく修飾キー専用の札として描く。
 */
export function modifierOnlyLabel(key: string): string {
  return key;
}

/**
 * `reduce()` 呼び出し前後の `EditorState` から、この1打が帯上でどう見えるべきかを分類する。
 * vimエンジン自体（計画3〜6で完成済み）には一切手を加えず、`usedKeystrokes` と
 * `pending.rawKeys` の変化だけから判定する。
 *
 * - 未習（`unlearned`）: `usedKeystrokes` が変化しない打鍵は、`reduce()` 内部で
 *   常にこの1パターンのみ（`interpret()` が `unlearned` を返した場合）。
 * - 保留（`pending`）: 処理後も `pending.rawKeys` が残っている（オペレータ待ち・カウント
 *   入力中・`f`/`t` の対象待ち等）。
 * - 確定（`confirmed`）: 上記のいずれでもない。直前が保留中だった場合は、その保留分の
 *   生キー列にこの1打を加えたものを技として1枠にまとめる（`d`+`w` → `dw`）。
 */
export function classifyKeystrokeOutcome(
  before: EditorState,
  after: EditorState,
  keystroke: Keystroke,
): KeystrokeOutcome {
  if (after.usedKeystrokes === before.usedKeystrokes) {
    return { kind: "unlearned", label: keystrokeNotation(keystroke) };
  }

  if (after.pending.rawKeys.length > 0) {
    return { kind: "pending", label: keysToLabel(after.pending.rawKeys) };
  }

  const label =
    before.pending.rawKeys.length > 0
      ? keysToLabel([...before.pending.rawKeys, keystroke])
      : keystrokeNotation(keystroke);
  return { kind: "confirmed", label };
}

let nextFrameId = 1;

/** テスト等でID採番を決定的にするための内部リセット（本番コードからは呼ばない）。 */
export function resetStripFrameIdForTests(): void {
  nextFrameId = 1;
}

/**
 * 帯へ1打分の結果を積む。保留中の枠は新しい枠を増やさず、同じ枠のラベルを
 * 更新し続ける（`d` → `dw` のように育つ）。確定・未習は必ず独立した枠になる。
 * 直近 `STRIP_CAPACITY` 打を超えた分は左端から落ちる。
 */
export function pushStripFrame(
  strip: readonly StripFrame[],
  outcome: KeystrokeOutcome,
): readonly StripFrame[] {
  const last = strip[strip.length - 1];

  if (outcome.kind === "pending" && last?.state === "pending") {
    const updated = [...strip.slice(0, -1), { ...last, label: outcome.label }];
    return updated.slice(-STRIP_CAPACITY);
  }

  if (outcome.kind === "confirmed" && last?.state === "pending") {
    const updated = [
      ...strip.slice(0, -1),
      { id: last.id, label: outcome.label, state: "learned" as const },
    ];
    return updated.slice(-STRIP_CAPACITY);
  }

  const state: StripFrameState =
    outcome.kind === "confirmed"
      ? "learned"
      : outcome.kind === "pending"
        ? "pending"
        : outcome.kind === "modifier"
          ? "modifier"
          : "unlearned";
  const frame: StripFrame = { id: nextFrameId, label: outcome.label, state };
  nextFrameId += 1;
  return [...strip, frame].slice(-STRIP_CAPACITY);
}
